import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { ReactNode, useState } from 'react';

import { AWS_CLI_ACCELERATED_ENDPOINT_OPTION } from './cliInstructionItNotes';

type SourceMode = 'recursive' | 'single_file';

type SourceModeLabels = {
  recursive: string;
  singleFile: string;
};

type AwsUploadCommandGeneratorProps = {
  copyableCommandBlock: (command: string) => ReactNode;
  destination: string;
  footerContent?: ReactNode;
  intro: string;
  invalidSourceText?: string;
  isSourceValid?: (source: string) => boolean;
  sourcePathInstructions?: ReactNode;
  sourceLabels: SourceModeLabels;
  sourcePlaceholders?: SourceModeLabels;
  uploadOptions?: string[];
};

const sourceModeDescriptions: Record<SourceMode, string> = {
  recursive: 'Folder mode uploads every nested file in the selected path.',
  single_file: 'File mode uploads only the single file you enter.',
};

const buildAwsUploadCommand = ({
  destination,
  recursive,
  source,
  uploadOptions = [],
}: {
  destination: string;
  recursive: boolean;
  source: string;
  uploadOptions?: string[];
}) => {
  const trimmedSource = source.trim();

  const normalizedSource =
    trimmedSource.length >= 2 && trimmedSource.startsWith('"') && trimmedSource.endsWith('"')
      ? trimmedSource.slice(1, -1).trim()
      : trimmedSource;

  if (!normalizedSource) {
    return '';
  }

  const isS3Source = normalizedSource.toLowerCase().startsWith('s3://');

  return [
    'aws s3 cp',
    `"${normalizedSource}"`,
    destination,
    ...uploadOptions,
    // AWS CLI applies --endpoint-url to source listing as well as destination writes.
    // For S3-to-S3 copies, skip acceleration unless the source bucket is known to support it.
    isS3Source ? undefined : AWS_CLI_ACCELERATED_ENDPOINT_OPTION,
    recursive ? '--recursive' : undefined,
  ]
    .filter(Boolean)
    .join(' ');
};

const AwsUploadCommandGenerator = ({
  copyableCommandBlock,
  destination,
  footerContent,
  intro,
  invalidSourceText,
  isSourceValid,
  sourcePathInstructions,
  sourceLabels,
  sourcePlaceholders,
  uploadOptions,
}: AwsUploadCommandGeneratorProps) => {
  const [source, setSource] = useState('');
  const [mode, setMode] = useState<SourceMode>('recursive');
  const recursive = mode === 'recursive';
  const trimmedSource = source.trim();
  const sourceIsValid = trimmedSource ? (isSourceValid ? isSourceValid(trimmedSource) : true) : true;
  const command =
    trimmedSource && sourceIsValid ? buildAwsUploadCommand({ destination, recursive, source, uploadOptions }) : '';

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        {intro}
      </Typography>
      {sourcePathInstructions}
      <Box
        sx={(theme) => ({
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          borderRadius: theme.shape.borderRadius,
          backgroundColor:
            theme.palette.mode === 'light'
              ? alpha(theme.palette.primary.light, 0.08)
              : alpha(theme.palette.primary.main, 0.12),
          px: 1.5,
          py: 1.5,
        })}
      >
        <Stack spacing={1.5}>
          <FormControl size="small">
            <FormLabel
              id="aws-upload-source-mode-label"
              sx={{ color: 'text.primary', fontSize: 13, fontWeight: 700, mb: 1 }}
            >
              Choose what you want to upload
            </FormLabel>
            <ToggleButtonGroup
              aria-labelledby="aws-upload-source-mode-label"
              role="radiogroup"
              exclusive
              size="small"
              value={mode}
              onChange={(_event, newValue: SourceMode | null) => {
                if (!newValue) {
                  return;
                }

                setMode(newValue);
              }}
              sx={{
                alignSelf: { xs: 'stretch', sm: 'flex-start' },
                '& .MuiToggleButton-root': {
                  px: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                },
              }}
            >
              <ToggleButton value="recursive" role="radio" aria-checked={recursive}>
                Folder
              </ToggleButton>
              <ToggleButton value="single_file" role="radio" aria-checked={!recursive}>
                File
              </ToggleButton>
            </ToggleButtonGroup>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            {sourceModeDescriptions[mode]}
          </Typography>
          <TextField
            label={recursive ? sourceLabels.recursive : sourceLabels.singleFile}
            value={source}
            onChange={(event) => setSource(event.target.value)}
            placeholder={recursive ? sourcePlaceholders?.recursive : sourcePlaceholders?.singleFile}
            error={Boolean(trimmedSource) && !sourceIsValid}
            helperText={Boolean(trimmedSource) && !sourceIsValid ? invalidSourceText : undefined}
            fullWidth
          />
        </Stack>
      </Box>
      {command ? (
        <>
          <Alert severity="info" variant="outlined">
            Copy this command and run it in the same terminal window. Keep that terminal open until the command
            finishes.
          </Alert>
          {copyableCommandBlock(command)}
          <Typography variant="body2" color="text.secondary">
            What success looks like: AWS CLI prints each copied file and then returns you to the command prompt. If the
            command stops with an error, keep the terminal output and ask your AWS administrator or Nucleai support to
            review it.
          </Typography>
        </>
      ) : trimmedSource && !sourceIsValid ? (
        <Typography variant="body2" color="text.secondary">
          Enter an S3 URI to generate the command.
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Add a source path to generate the command.
        </Typography>
      )}
      <Accordion disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2">Where will my slides be uploaded?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="caption" color="text.secondary">
            Slides are copied into {destination}. Nucleai tracks this study upload folder for new files.
          </Typography>
        </AccordionDetails>
      </Accordion>
      {footerContent}
    </Stack>
  );
};

export default AwsUploadCommandGenerator;
